import type DestinationsShow from "@travily/types/pages/DestinationsShow";

export type Costs = NonNullable<DestinationsShow["costs"]>;
export type Household = Costs["household"];
type Category = Costs["categories"][number];
type Line = Category["items"][number];
type Bounds = { p25_usd_minor: number | null; p50_usd_minor: number | null; p75_usd_minor: number | null };

/**
 * The household scaling, mirrored in the browser — the twin of
 * `WorldCosts::Calculations::Household`, coefficient for coefficient.
 *
 * It exists so a family changing "who is going" sees the figure move at once
 * instead of after a round trip; the server's answer replaces this one the
 * moment it lands. Only the money is restated: grades, coverage and confidence
 * are re-scored against the evidence, which is the panel's business and not
 * something a browser can honestly guess at.
 *
 * The coefficients are duplicated deliberately and the duplication is held by
 * `spec/fixtures/world_costs/household_parity.json` — both sides answer the
 * same cases with the same integers, or one of the two suites fails.
 */

/** Every figure the panel publishes is in USD minor units; the display currency is the leaf's business. */
export const PUBLISHED_CURRENCY = "USD";

/** Everything scales off the pinned reference family (Calculations::ReferenceHousehold). */
export const REFERENCE_HOUSEHOLD: Household = {
  adults: 2,
  child_ages: [6, 10],
  nights: 30,
  tier: "mid",
  month: null,
};

const REFERENCE_NIGHTS = 30;
const ADULT_SHARE = 1;
/** Concessionary child fares are near-universal and typically half the adult one. */
const CHILD_TRANSPORT_SHARE = 0.5;
/** The USDA food-plan gradient against an adult: under six is a small eater, a teenager is not. */
const CHILD_FOOD_SHARES: Array<{ upTo: number; share: number }> = [
  { upTo: 5, share: 0.6 },
  { upTo: 12, share: 0.7 },
  { upTo: 17, share: 0.95 },
];

type Mode = "fixed" | "equivalentAdult" | "adult" | "child";

const MODES: Record<string, Mode> = {
  housing: "fixed",
  connectivity: "fixed",
  food: "equivalentAdult",
  health: "equivalentAdult",
  services: "equivalentAdult",
  transport: "adult",
  family: "child",
};

const TIER_BOUND: Record<Household["tier"], keyof Bounds> = {
  low: "p25_usd_minor",
  mid: "p50_usd_minor",
  high: "p75_usd_minor",
};

function childFoodShare(age: number): number {
  return CHILD_FOOD_SHARES.find((band) => age <= band.upTo)?.share ?? ADULT_SHARE;
}

function equivalentAdults(household: Household): number {
  return household.adults * ADULT_SHARE + household.child_ages.reduce((sum, age) => sum + childFoodShare(age), 0);
}

function riders(household: Household): number {
  return household.adults * ADULT_SHARE + household.child_ages.length * CHILD_TRANSPORT_SHARE;
}

function nightsShare(household: Household): number {
  return household.nights / REFERENCE_NIGHTS;
}

/** What one category multiplies by for this household, relative to the reference. */
export function factorFor(categoryKey: string, household: Household): number {
  const mode = MODES[categoryKey] ?? "fixed";
  const modeFactor =
    mode === "equivalentAdult"
      ? equivalentAdults(household) / equivalentAdults(REFERENCE_HOUSEHOLD)
      : mode === "adult"
        ? riders(household) / riders(REFERENCE_HOUSEHOLD)
        : mode === "child"
          ? household.child_ages.length / REFERENCE_HOUSEHOLD.child_ages.length
          : 1;
  return modeFactor * nightsShare(household);
}

function scale(value: number | null, factor: number): number | null {
  return value === null ? null : Math.round(value * factor);
}

function scaled(bounds: Bounds, factor: number): Bounds {
  return {
    p25_usd_minor: scale(bounds.p25_usd_minor, factor),
    p50_usd_minor: scale(bounds.p50_usd_minor, factor),
    p75_usd_minor: scale(bounds.p75_usd_minor, factor),
  };
}

/** The band is not re-cut, only re-pointed: a tier reads the same spread at a different place in it. */
function tiered(bounds: Bounds, tier: Household["tier"]): Bounds {
  return { ...bounds, p50_usd_minor: bounds[TIER_BOUND[tier]] };
}

function sumBound(categories: Bounds[], bound: keyof Bounds): number | null {
  let total = 0;
  for (const category of categories) {
    const value = category[bound];
    if (value === null) return null;
    total += value;
  }
  return total;
}

// An item crosses as a midpoint alone, so a tier cannot re-point it here the
// way it re-points a category: the preview scales it and leaves it at the
// percentile the panel published, and the server's answer re-points it.
function scaleItem(item: Line, factor: number): Line {
  return { ...item, p50_usd_minor: scale(item.p50_usd_minor, factor) };
}

function scaleCategory(category: Category, household: Household): Category {
  const factor = factorFor(category.key, household);
  return {
    ...category,
    ...tiered(scaled(category, factor), household.tier),
    items: category.items.map((item) => scaleItem(item, factor)),
  };
}

function shareOf(value: number | null, total: number | null): number | null {
  if (value === null || total === null || total === 0) return null;
  return value / total;
}

/** The shape is the place's, the level is this household's. */
function relevel(monthly: number[] | null, from: number | null, to: number | null): number[] | null {
  if (monthly === null || from === null || to === null || from === 0) return monthly;
  const factor = to / from;
  return monthly.map((value) => Math.round(value * factor));
}

export function applyHousehold(costs: Costs, household: Household): Costs {
  const categories = costs.categories.map((category) => scaleCategory(category, household));
  const total = {
    ...costs.total,
    p25_usd_minor: sumBound(categories, "p25_usd_minor"),
    p50_usd_minor: sumBound(categories, "p50_usd_minor"),
    p75_usd_minor: sumBound(categories, "p75_usd_minor"),
  };

  return {
    ...costs,
    household,
    total,
    categories: categories.map((category) => ({
      ...category,
      share: shareOf(category.p50_usd_minor, total.p50_usd_minor),
    })),
    monthly: relevel(costs.monthly, costs.total.p50_usd_minor, total.p50_usd_minor),
  };
}
